/**
 * React Query hooks for the teaching video pipeline.
 *
 * Polling pattern mirrors useEvaluationJob.ts — the result query polls
 * every 2 seconds while status is 'uploaded' or 'processing', and stops
 * when the terminal state ('completed' or 'failed') is reached.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  uploadTeachingVideo,
  submitPasteNotes,
  triggerProcessing,
  getTeachingVideoResult,
  getTeachingVideoTranscript,
} from '@/services/teachingVideoApi';
import type { TeachingVideoStatus } from '@/types/teachingVideo';

const TERMINAL_STATUSES = new Set<TeachingVideoStatus>(['completed', 'failed']);
const POLLING_INTERVAL_MS = 2000;

// ---------------------------------------------------------------------------
// Polling query — coach-side result
// ---------------------------------------------------------------------------

/**
 * Poll a teaching video result until it reaches a terminal state.
 *
 * Polling stops automatically when status is 'completed' or 'failed'.
 * Pass undefined to disable the query entirely.
 */
export function useTeachingVideoResult(id: string | undefined) {
  return useQuery({
    queryKey: ['teaching-video-result', id],
    queryFn: () => getTeachingVideoResult(id!),
    enabled: !!id,
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      if (!status || TERMINAL_STATUSES.has(status)) return false;
      return POLLING_INTERVAL_MS;
    },
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Transcript query — athlete-facing read
// ---------------------------------------------------------------------------

/**
 * Fetch the transcript of a teaching video (athlete-facing).
 * No polling — transcripts are static once the video is completed.
 */
export function useTeachingVideoTranscript(id: string | undefined) {
  return useQuery({
    queryKey: ['teaching-video-transcript', id],
    queryFn: () => getTeachingVideoTranscript(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Upload mutation
// ---------------------------------------------------------------------------

/** Mutation for uploading a teaching video or audio file. */
export function useUploadTeachingVideo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      sport,
      movement,
    }: {
      file: File;
      sport: string;
      movement: string;
    }) => uploadTeachingVideo(file, sport, movement),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['teaching-video-result', data.id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Paste-notes mutation
// ---------------------------------------------------------------------------

/** Mutation for submitting pasted coaching notes. */
export function usePasteNotes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      text,
      sport,
      movement,
    }: {
      text: string;
      sport: string;
      movement: string;
    }) => submitPasteNotes(text, sport, movement),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['teaching-video-result', data.id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Trigger processing mutation
// ---------------------------------------------------------------------------

/** Mutation to trigger async extraction after upload or paste-notes. */
export function useTriggerProcessing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => triggerProcessing(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['teaching-video-result', id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Link teaching video to template
// ---------------------------------------------------------------------------
//
// `useLinkTeachingVideo` lived here with ZERO call sites (audit item `FE-4`).
// It was written for the case it never covered: adding coach instruction to an
// EXISTING template, where the link call was simply missing, so the teaching
// video was never attached, the template page had no recording to play, and
// the row was eventually removed by `_purge_unlinked_teaching_videos`.
//
// PR-8 fixed the real gap in `CreateTemplate`'s edit branch and DELETED the
// hook. Both branches of that save call `linkTeachingVideoToTemplate` directly,
// because the link is one step of an imperative save sequence, not something a
// render needs to subscribe to — a mutation hook there would have been a second
// way to do the same thing, which is how the first one came to be dead.
// Deleted rather than kept "in case": see `TV-19`'s rule for a dead path.
